import { useEffect, useState } from "react";
import { Loader2, ShieldCheck, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";

const SetupInfo = ({ setup }) => (
  <div className="mt-6 rounded-2xl border border-gray-200 bg-oath-bg/60 p-4" data-testid="twofa-setup-panel">
    <div className="flex items-start gap-4">
      <img src={setup.qr} alt="2FA QR code" className="h-36 w-36 shrink-0 rounded-xl border border-gray-200 bg-white p-1" data-testid="twofa-qr" />
      <div className="text-xs leading-relaxed text-oath-secondary">
        <p>Scan this code with an authenticator app (Google Authenticator, 1Password, Authy, Microsoft Authenticator).</p>
        <p className="mt-3 font-mono-label text-[10px] text-oath-muted">OR ENTER THIS KEY</p>
        <code className="mt-1 block break-all rounded-md bg-white px-2 py-1 font-mono text-[11px] text-oath-ink" data-testid="twofa-secret">
          {setup.secret}
        </code>
      </div>
    </div>
  </div>
);

export const TwoFactorStep = ({ challenge, onDone }) => {
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!challenge.setup_required) {
      return;
    }
    api.post("/auth/2fa/setup", { mfa_token: challenge.mfa_token })
      .then(({ data }) => setSetup(data))
      .catch((e) => toast.error(formatApiError(e)));
  }, [challenge]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/2fa/verify", { mfa_token: challenge.mfa_token, code });
      toast.dismiss();
      onDone(data.user);
    } catch (err) {
      setError(formatApiError(err));
      setCode("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="twofa-form">
      <span className="grid h-11 w-11 place-items-center rounded-xl bg-oath-accent text-white">
        {challenge.setup_required ? <Smartphone className="h-5 w-5" /> : <ShieldCheck className="h-5 w-5" />}
      </span>
      <h1 className="mt-6 font-display text-3xl font-bold tracking-tight text-oath-ink">
        {challenge.setup_required ? "Set up two-factor" : "Two-factor check"}
      </h1>
      <p className="mt-3 text-sm leading-relaxed text-oath-secondary">
        {challenge.setup_required
          ? "Two-factor authentication is required for every ResidenceProof account."
          : `Enter the 6-digit code from your authenticator app for ${challenge.email}.`}
      </p>
      {challenge.setup_required && (setup ? <SetupInfo setup={setup} /> : <Loader2 className="mt-6 h-5 w-5 animate-spin text-oath-muted" />)}
      {error && <div className="mt-5 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="twofa-error">{error}</div>}
      <label className="mt-6 block font-mono-label text-[10px] font-semibold text-oath-secondary">6-DIGIT CODE</label>
      <Input
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus
        placeholder="000000"
        className="mt-2 h-12 text-center font-mono text-xl tracking-[0.5em]"
        data-testid="twofa-code-input"
      />
      <button
        type="submit"
        disabled={loading || code.length !== 6}
        data-testid="twofa-submit-button"
        className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-oath-ink px-6 py-3.5 font-mono-label text-[12px] font-semibold text-white transition-colors hover:bg-black disabled:opacity-50"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : challenge.setup_required ? "VERIFY & ENABLE" : "VERIFY"}
      </button>
    </form>
  );
};

export default TwoFactorStep;
